/**
 * @module TTProductMainInfoCustom.View
 * Backbone.View (not SCView): renders the custom PDP main info strip from PDP.getItemInfo().
 */
define('TTProductMainInfoCustom.View'
,	[
		'tt_product_main_info_custom.tpl'
	,	'Backbone'
	]
,	function (
		tt_product_main_info_custom_tpl
	,	Backbone
	)
{
	'use strict';

	function getRawField(item, key)
	{
		if (!item || !key)
		{
			return undefined;
		}
		try
		{
			if (typeof item.get === 'function')
			{
				var got = item.get(key);
				if (got !== undefined)
				{
					return got;
				}
			}
			if (item.attributes && Object.prototype.hasOwnProperty.call(item.attributes, key))
			{
				return item.attributes[key];
			}
			if (Object.prototype.hasOwnProperty.call(item, key))
			{
				return item[key];
			}
			if (key in item)
			{
				return item[key];
			}
		}
		catch (e)
		{
			return undefined;
		}
		return undefined;
	}

	/** NetSuite multiline / rich text often arrives as { text: '...' } or { value: '...' }. */
	function coerceRichTextFieldToString (raw)
	{
		if (raw === null || raw === undefined)
		{
			return '';
		}
		if (typeof raw === 'string')
		{
			return raw;
		}
		if (typeof raw === 'number')
		{
			return String(raw);
		}
		if (typeof raw === 'object')
		{
			if (typeof raw.text === 'string')
			{
				return raw.text;
			}
			if (typeof raw.value === 'string')
			{
				return raw.value;
			}
			if (raw.value && typeof raw.value === 'object' && typeof raw.value.text === 'string')
			{
				return raw.value.text;
			}
			if (raw.defaultValue && typeof raw.defaultValue === 'object' && typeof raw.defaultValue.text === 'string')
			{
				return raw.defaultValue.text;
			}
		}
		return '';
	}

	/**
	 * Collapse NBSP entities / char and trim; empty if only whitespace/NBSP (ignore as brand).
	 */
	function normalizeBrandDisplayString (str)
	{
		if (str == null || typeof str !== 'string')
		{
			return '';
		}
		var t = str
			.replace(/&nbsp;/gi, ' ')
			.replace(/&#160;/gi, ' ')
			.replace(/&#x0*A0;/gi, ' ')
			.replace(/\u00a0/g, ' ')
			.replace(/<[^>]*>/g, ' ');
		t = t.replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
		return t;
	}

	/**
	 * Brand display name from custitem_tt_brand_name (plain string or list/record-style object).
	 */
	function coerceBrandNameToString (raw)
	{
		var out = '';
		if (raw === null || raw === undefined)
		{
			return '';
		}
		if (typeof raw === 'string')
		{
			out = raw;
		}
		else if (typeof raw === 'number')
		{
			out = String(raw);
		}
		else if (typeof raw === 'object')
		{
			if (typeof raw.name === 'string' && raw.name.replace(/^\s+|\s+$/g, '').length)
			{
				out = raw.name;
			}
			else
			{
				out = coerceRichTextFieldToString(raw);
			}
		}
		return normalizeBrandDisplayString(out);
	}

	/**
	 * NetSuite "Store Description" (native item field). JSON shape varies: record id `storeDescription`,
	 * commerce/search `storedescription`, SC field maps `keyMapping_*`, optional snake_case.
	 */
	var STORE_DESCRIPTION_FIELD_KEYS = [
		'storeDescription'
	,	'storedescription'
	,	'store_description'
	,	'keyMapping_storedescription'
	,	'keyMapping_storeDescription'
	];

	function pickStoreDescriptionRaw (sources)
	{
		var seen = [];
		var s;
		var si;
		var ki;
		var src;
		var raw;
		for (s = 0; s < sources.length; s++)
		{
			src = sources[s];
			if (!src || seen.indexOf(src) >= 0)
			{
				continue;
			}
			seen.push(src);
			for (ki = 0; ki < STORE_DESCRIPTION_FIELD_KEYS.length; ki++)
			{
				raw = getRawField(src, STORE_DESCRIPTION_FIELD_KEYS[ki]);
				if (raw !== undefined && raw !== null && raw !== '')
				{
					return raw;
				}
			}
		}
		return undefined;
	}

	function resolveStoreDescriptionString (itemModel, plain, info)
	{
		var sources = [];
		if (itemModel)
		{
			sources.push(itemModel);
		}
		if (plain && plain !== itemModel)
		{
			sources.push(plain);
		}
		if (info && typeof info === 'object')
		{
			if (info.item && info.item !== itemModel && info.item !== plain)
			{
				sources.push(info.item);
			}
			if (info.line && info.line.item && info.line.item !== itemModel && info.line.item !== plain)
			{
				sources.push(info.line.item);
			}
			sources.push(info);
		}
		var raw = pickStoreDescriptionRaw(sources);
		if (raw === undefined || raw === null || raw === '')
		{
			return '';
		}
		var str = coerceRichTextFieldToString(raw);
		return str.replace(/^\s+|\s+$/g, '').length ? str : '';
	}

	function escapeHtmlAttribute (value)
	{
		if (value === null || value === undefined)
		{
			return '';
		}
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/"/g, '&quot;')
			.replace(/</g, '&lt;');
	}

	function buildBrandImgTagFromUrl (url, alt)
	{
		var srcEsc = escapeHtmlAttribute(url);
		var altEsc = escapeHtmlAttribute(alt);
		return '<img class="product-main-info-custom__brand-img" src="' + srcEsc + '" alt="' + altEsc + '" loading="lazy" width="32" height="32" />';
	}

	/**
	 * Brand image for the template: full `<img ...>` string from NetSuite (e.g. custitem), or URL / file JSON → we wrap as <img>.
	 */
	function toBrandImageHtml (raw, altText)
	{
		if (raw === null || raw === undefined || raw === '')
		{
			return '';
		}
		var alt = altText ? String(altText).replace(/^\s+|\s+$/g, '') : '';

		if (typeof raw === 'string')
		{
			var t = raw.replace(/^\s+|\s+$/g, '');
			if (!t.length)
			{
				return '';
			}
			if (/^<img\b/i.test(t))
			{
				return t;
			}
			return buildBrandImgTagFromUrl(t, alt);
		}

		if (Array.isArray(raw) && raw.length)
		{
			return toBrandImageHtml(raw[0], altText);
		}

		if (typeof raw === 'object')
		{
			var tx;
			if (typeof raw.text === 'string')
			{
				tx = raw.text.replace(/^\s+|\s+$/g, '');
				if (tx.length)
				{
					if (/^<img\b/i.test(tx))
					{
						return tx;
					}
					if (tx.indexOf('http') === 0 || tx.indexOf('//') === 0 || tx.indexOf('/') === 0)
					{
						return buildBrandImgTagFromUrl(tx, alt);
					}
				}
			}
			var u =
				raw.url
			||	raw.src
			||	raw.href
			||	raw.absoluteURL
			||	raw.absoluteUrl
			||	raw.thumbnailurl
			||	raw.thumbnailUrl;
			if (u)
			{
				return buildBrandImgTagFromUrl(String(u).replace(/^\s+|\s+$/g, ''), alt);
			}
		}

		return '';
	}

	/**
	 * If storedescription contains markup, render unescaped in the template (NetSuite-sourced content).
	 * Plain text stays escaped. Heuristic: tag-like `<letter...>` — avoids most false positives from "< 5".
	 */
	function stringLooksLikeHtml (str)
	{
		if (!str || typeof str !== 'string')
		{
			return false;
		}
		return /<[a-z][\s\S]*>/i.test(str);
	}

	return Backbone.View.extend({

		template: tt_product_main_info_custom_tpl

	,	className: 'product-main-info-custom'

	,	initialize: function initialize (options)
		{
			this.container = options && options.container;
			this._starRatingView = null;
			this._starRatingMountToken = 0;
			/** Last successful template context (add-to-cart / PDP refresh can briefly clear getItemInfo). */
			this._lastGoodProductContext = null;
			/** Plain item attributes for StarRating when getRawPdpItem() is temporarily null (same window as cached context). */
			this._lastRatingItemSnapshot = null;
			this._starRatingRetryToken = 0;
			this._starRatingRetryBatchToken = null;
		}

	,	teardownStarRating: function teardownStarRating ()
		{
			if (this._starRatingView && typeof this._starRatingView.remove === 'function')
			{
				try
				{
					this._starRatingView.remove();
				}
				catch (e)
				{
					/* ignore */
				}
			}
			this._starRatingView = null;
		}

	,	getRawPdpItem: function getRawPdpItem ()
		{
			var pdp = this.getPdp();
			if (!pdp || typeof pdp.getItemInfo !== 'function')
			{
				return null;
			}
			var info = pdp.getItemInfo();
			if (!info)
			{
				return null;
			}
			return info.item || (info.line && info.line.item) || null;
		}

	,	updateRatingItemSnapshot: function updateRatingItemSnapshot (item)
		{
			if (!item)
			{
				return;
			}
			try
			{
				var snap;
				if (typeof item.toJSON === 'function')
				{
					snap = item.toJSON();
				}
				else if (item.attributes && typeof item.attributes === 'object')
				{
					snap = {};
					var k;
					for (k in item.attributes)
					{
						if (Object.prototype.hasOwnProperty.call(item.attributes, k))
						{
							snap[k] = item.attributes[k];
						}
					}
				}
				else
				{
					snap = item;
				}
				if (snap && typeof snap === 'object')
				{
					this._lastRatingItemSnapshot = snap;
				}
			}
			catch (eSnap)
			{
				/* ignore */
			}
		}

	/**
	 * When PDP drops item briefly after add-to-cart, retry mounting stars after live data returns.
	 */
	,	scheduleMountStarRatingRetries: function scheduleMountStarRatingRetries ()
		{
			var self = this;
			var token = this._starRatingRetryToken;
			if (this._starRatingRetryBatchToken === token)
			{
				return;
			}
			this._starRatingRetryBatchToken = token;
			var delays = [80, 250, 600, 1200];
			var i;
			for (i = 0; i < delays.length; i++)
			{
				(function (ms) {
					setTimeout(function () {
						if (token !== self._starRatingRetryToken)
						{
							return;
						}
						if (!self.$('.product-details-full-rating').length)
						{
							return;
						}
						self.mountStarRatingView();
					}, ms);
				}(delays[i]));
			}
		}

	/**
	 * data-view placeholders are only processed by SC composite views; this Backbone view must load StarRating via AMD.
	 */
	,	mountStarRatingView: function mountStarRatingView ()
		{
			var self = this;
			var $slot = this.$('.product-details-full-rating');
			if (!$slot.length)
			{
				return;
			}
			var liveItem = this.getRawPdpItem();
			if (liveItem)
			{
				this.updateRatingItemSnapshot(liveItem);
			}
			var item = liveItem;
			if (!item && this._lastRatingItemSnapshot)
			{
				item = new Backbone.Model(this._lastRatingItemSnapshot);
			}
			if (!item)
			{
				this.scheduleMountStarRatingRetries();
				return;
			}

			var ratingModel;
			if (typeof item.get === 'function' && typeof item.toJSON === 'function')
			{
				ratingModel = item;
			}
			else
			{
				var attrs = item.attributes && typeof item.attributes === 'object' ? item.attributes : item;
				ratingModel = new Backbone.Model(attrs);
			}

			var token = (this._starRatingMountToken = (this._starRatingMountToken || 0) + 1);

			if (typeof require !== 'function')
			{
				return;
			}

			function instantiateStarRating (StarRatingView)
			{
				if (token !== self._starRatingMountToken)
				{
					return;
				}
				var $s = self.$('.product-details-full-rating');
				if (!$s.length)
				{
					return;
				}
				try
				{
					self.teardownStarRating();
					if (token !== self._starRatingMountToken)
					{
						return;
					}
					self._starRatingView = new StarRatingView({
						model: ratingModel
					,	showRatingCount: true
					,	showSchemaInfo: true
					});
					$s.empty();
					$s.append(self._starRatingView.render().el);
				}
				catch (errMount)
				{
					/* ignore */
				}
			}

			require(
				['GlobalViews.StarRating.View']
			,	instantiateStarRating
			,	function ()
				{
					require(
						['Global.StarRating.View']
					,	instantiateStarRating
					,	function ()
					{
						/* StarRating module not in bundle */
					}
					);
				}
			);
		}

	,	remove: function remove ()
		{
			this.teardownStarRating();
			this._lastGoodProductContext = null;
			this._lastRatingItemSnapshot = null;
			this._starRatingRetryBatchToken = null;
			this._starRatingRetryToken = (this._starRatingRetryToken || 0) + 1;
			return Backbone.View.prototype.remove.call(this);
		}

	,	getPdp: function getPdp ()
		{
			return this.container && this.container.getComponent('PDP');
		}

	,	render: function render ()
		{
			this._starRatingRetryToken = (this._starRatingRetryToken || 0) + 1;
			this.teardownStarRating();
			try
			{
				this.$el.html(this.template(this.getContext()));
				this.mountStarRatingView();
			}
			catch (err)
			{
				/* ignore */
			}
			return this;
		}

	,	cloneTemplateContext: function cloneTemplateContext (ctx)
		{
			var out = {};
			var k;
			for (k in ctx)
			{
				if (Object.prototype.hasOwnProperty.call(ctx, k))
				{
					out[k] = ctx[k];
				}
			}
			return out;
		}

	,	getContext: function getContext ()
		{
			var pdp = this.container && this.container.getComponent('PDP');
			if (!pdp)
			{
				if (this._lastGoodProductContext)
				{
					return this.cloneTemplateContext(this._lastGoodProductContext);
				}
				return this.buildTemplateContext(null, null);
			}
			if (typeof pdp.getItemInfo !== 'function')
			{
				if (this._lastGoodProductContext)
				{
					return this.cloneTemplateContext(this._lastGoodProductContext);
				}
				return this.buildTemplateContext(null, null);
			}

			var info = pdp.getItemInfo();
			var ctx = this.buildTemplateContext(pdp, info);

			if (ctx && ctx.hasItem)
			{
				this._lastGoodProductContext = this.cloneTemplateContext(ctx);
				var liveForSnap = info && (info.item || (info.line && info.line.item));
				if (liveForSnap)
				{
					this.updateRatingItemSnapshot(liveForSnap);
				}
				return ctx;
			}

			/* Transient gap after add-to-cart / PDP re-render: getItemInfo() or item missing briefly. */
			if (this._lastGoodProductContext && (!info || !(info.item || (info.line && info.line.item))))
			{
				return this.cloneTemplateContext(this._lastGoodProductContext);
			}

			return ctx;
		}

	,	buildTemplateContext: function buildTemplateContext (pdp, info)
		{
			if (!info)
			{
				return {
					hasItem: false
				};
			}

			var item = info.item || (info.line && info.line.item);
			if (!item)
			{
				return {
					hasItem: false
				};
			}

			var plain = item;
			if (typeof item.toJSON === 'function')
			{
				try
				{
					plain = item.toJSON() || item;
				}
				catch (e)
				{
					plain = item.attributes || item;
				}
			}
			else if (item.attributes && typeof item.attributes === 'object')
			{
				plain = item.attributes;
			}

			/* PDP H1: NetSuite Store Display Name (`storedisplayname`), then pageHeader / name / SKU. */
			var pageTitle =
				getRawField(plain, 'storedisplayname')
			||	getRawField(plain, 'storeDisplayName')
			||	getRawField(plain, 'storedisplayname2')
			||	getRawField(plain, 'storeDisplayName2')
			||	getRawField(plain, 'pageHeader')
			||	getRawField(plain, '_name')
			||	getRawField(plain, 'itemid')
			||	'';
			var title = pageTitle;
			var sku = getRawField(plain, 'itemid') || '';
			var model = getRawField(plain, 'custitem_tt_model') || '';
			/* {{brand}} ← custitem_tt_brand_name only (unwrap NS list/object shapes). */
			var brand = coerceBrandNameToString(getRawField(plain, 'custitem_tt_brand_name'));
			/* Brand image: only custitem_tt_brand_image_01 (full <img> or URL / file JSON → `brandImage` HTML). */
			var brandImageRaw = getRawField(plain, 'custitem_tt_brand_image_01');
			var brandImage = toBrandImageHtml(brandImageRaw, brand);
			/* descriptionHtml ← NetSuite Store Description (all common SC/JSON keys). */
			var descStr = resolveStoreDescriptionString(item, plain, info);
			var descriptionHtml = '';
			var descriptionIsRich = false;
			if (descStr.replace(/^\s+|\s+$/g, '').length)
			{
				descriptionHtml = descStr;
				descriptionIsRich = stringLooksLikeHtml(descStr);
			}

			var priceFormatted = '';
			var rawComparePriceFormatted = '';
			var detail = getRawField(plain, 'onlinecustomerprice_detail');
			if (detail && typeof detail === 'object')
			{
				priceFormatted = detail.onlinecustomerprice_formatted || '';
				rawComparePriceFormatted =
					detail.compare_price_formatted
				||	detail.list_price_formatted
				||	detail.msrp_formatted
				||	'';
			}

			/* 1.0.6: honour NetSuite price visibility (Personalized Catalog Views
			   "Disable Purchase and Hide Price", Require Login for Pricing).
			   The Items API still returns the amount, so the block must check
			   ispricevisible itself. Only an explicit false hides the price. */
			var isPriceHidden = getRawField(plain, 'ispricevisible') === false;
			if (isPriceHidden)
			{
				priceFormatted = '';
				rawComparePriceFormatted = '';
			}

			var showCompare =
				!!rawComparePriceFormatted
			&&	rawComparePriceFormatted !== priceFormatted;

			var stockInfo = pdp && typeof pdp.getStockInfo === 'function' ? pdp.getStockInfo() : null;
			var isInStock = stockInfo && typeof stockInfo.isInStock !== 'undefined'
				? !!stockInfo.isInStock
				: !!getRawField(plain, 'isinstock');

			/* NetSuite item "Out of Stock Behavior" = allow back orders → isbackorderable true.
			   Out-of-stock + backorderable renders NO badge (item is still purchasable); the
			   "Out of Stock" badge only shows when the item genuinely cannot be ordered. */
			var isBackorderable = stockInfo && typeof stockInfo.isBackorderable !== 'undefined'
				? !!stockInfo.isBackorderable
				: !!getRawField(plain, 'isbackorderable');
			var showStockBadge = isInStock || !isBackorderable;

			var showMetaLine = !!(sku || model);
			var showPriceRow = !!(priceFormatted || showCompare);
			var showBrandRow = !!(brand || brandImage);

			return {
				hasItem: true
			,	title: title
			,	pageHeader: pageTitle
			,	sku: sku
			,	model: model
			,	brand: brand
			,	brandImage: brandImage
			,	showBrandRow: showBrandRow
			,	descriptionHtml: descriptionHtml
			,	descriptionIsRich: descriptionIsRich
			,	priceFormatted: priceFormatted
			,	comparePriceFormatted: showCompare ? rawComparePriceFormatted : ''
			,	showPriceRow: showPriceRow
			,	isPriceHidden: isPriceHidden
			,	showMetaLine: showMetaLine
			,	isInStock: isInStock
			,	isBackorderable: isBackorderable
			,	showStockBadge: showStockBadge
			,	_pmiItemInternalId: getRawField(plain, 'internalid')
			};
		}
	});
});
